import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { UserPicker } from "@/components/UserPicker";
import type { ResolvedPerson } from "@/lib/name-matching";
import type { TesterUser } from "@/services/testers";

interface PersonChipProps {
  person: ResolvedPerson;
  mappingId: string;
  personIndex: number;
  cognitoUsers: TesterUser[];
  isTestersLoading: boolean;
  isTestersError: boolean;
  onResolve: (mappingId: string, personIndex: number, user: TesterUser) => void;
  onRemove: (mappingId: string, personIndex: number) => void;
  onRetryTesters: () => void;
}

export function PersonChip({
  person,
  mappingId,
  personIndex,
  cognitoUsers,
  isTestersLoading,
  isTestersError,
  onResolve,
  onRemove,
  onRetryTesters,
}: PersonChipProps) {
  const isResolved = !!person.cognitoUserId;

  const dotColor =
    person.matchConfidence === "exact" ? "bg-green-400" :
    person.matchConfidence === "fuzzy" ? "bg-amber-400" :
    person.matchConfidence === "manual" ? "bg-blue-400" :
    "bg-muted-foreground";

  const displayName = isResolved ? person.cognitoUserName : person.excelName;
  const tooltip = isResolved
    ? `${person.excelName} → ${person.cognitoUserName} (${person.cognitoEmail})\nMatch: ${person.matchConfidence}`
    : `Unresolved: ${person.excelName}`;

  return (
    <span
      className={cn(
        "group/chip inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-md border text-xs",
        isResolved
          ? person.matchConfidence === "fuzzy"
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : "bg-muted/50 border-border text-foreground"
          : "bg-warning/10 border-warning/30 text-warning"
      )}
      title={tooltip}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", dotColor)} />
      <span className="truncate max-w-[130px]">{displayName}</span>

      <UserPicker
        mode="replace"
        onSelect={(u) => onResolve(mappingId, personIndex, u)}
        cognitoUsers={cognitoUsers}
        isLoading={isTestersLoading}
        isError={isTestersError}
        onRetry={onRetryTesters}
      />
      <button
        onClick={() => onRemove(mappingId, personIndex)}
        className={cn(
          "rounded-full p-0.5 transition-opacity shrink-0",
          isResolved
            ? "opacity-0 group-hover/chip:opacity-100 hover:bg-foreground/10"
            : "hover:bg-warning/20"
        )}
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}
